import { api, AuthExpiredError, type Measurement, type Settings } from '../lib/api';
import { outbox, offlineCache } from '../lib/outbox';
import { attachToVerified, type Site } from '../lib/cluster';
import { getState, setState } from './state';

/** Verified/manual sites absorb the radios of nearby estimates; remaining estimates are kept separately. */
export function mergeSites(all: Site[]): Site[] {
  const known = all.filter(s => s.reliability !== 'stimato');
  const est = all.filter(s => s.reliability === 'stimato');
  const { verified, extra } = attachToVerified(est, known);
  return [...verified, ...extra];
}

function handle(e: unknown) {
  if (e instanceof AuthExpiredError) setState({ authExpired: true });
  else setState({ offline: true });
}

export async function loadAll(): Promise<void> {
  const [cs, cst, cm] = await Promise.all([offlineCache.getSites(), offlineCache.getSettings(), offlineCache.getMeasurements()]);
  setState({ sites: mergeSites(cs ?? []), settings: cst ?? {}, measurements: cm ?? [], pending: await outbox.pending() });
  try {
    const [sites, settings, measurements] = await Promise.all([api.getSites(), api.getSettings(), api.getMeasurements()]);
    await Promise.all([offlineCache.setSites(sites), offlineCache.setSettings(settings), offlineCache.setMeasurements(measurements)]);
    setState({ sites: mergeSites(sites), settings, measurements, authExpired: false, offline: false });
  } catch (e) {
    handle(e);
  }
  await sync();
}

/** Push queued measurements, then refresh the list. Safe to call often. */
export async function sync(): Promise<void> {
  const r = await outbox.flush();
  const pending = await outbox.pending();
  // Only change the session flag on evidence: a 401/redirect sets it, a successful send clears it.
  setState({ pending, ...(r.authExpired ? { authExpired: true } : r.sent > 0 ? { authExpired: false } : {}) });
  if (r.sent > 0) {
    try {
      const measurements = await api.getMeasurements();
      await offlineCache.setMeasurements(measurements);
      setState({ measurements, offline: false });
    } catch (e) {
      handle(e);
    }
  }
}

export async function saveMeasurement(m: Measurement): Promise<void> {
  await outbox.enqueue(m);
  setState({ pending: await outbox.pending() });
  void sync();
}

export async function deleteMeasurement(id: string): Promise<void> {
  const queued = getState().pending.some(p => p.id === id);
  if (queued) {
    await outbox.remove(id);
    setState({ pending: await outbox.pending() });
  }
  if (getState().measurements.some(m => m.id === id)) {
    await api.deleteMeasurement(id);
    const measurements = getState().measurements.filter(m => m.id !== id);
    await offlineCache.setMeasurements(measurements);
    setState({ measurements });
  }
}

export async function saveSettings(patch: Settings): Promise<void> {
  const settings = { ...getState().settings, ...patch };
  setState({ settings, los: new Map() });
  await offlineCache.setSettings(settings);
  await api.putSettings(patch);
}

async function refreshSites(): Promise<void> {
  const sites = await api.getSites();
  await offlineCache.setSites(sites);
  setState({ sites: mergeSites(sites) });
}

export async function saveSite(s: Site): Promise<void> {
  await api.putSite(s);
  await refreshSites();
}

export async function deleteSite(id: string): Promise<void> {
  await api.deleteSite(id);
  await refreshSites();
}
